import { useEffect } from 'react';

/**
 * Holds a screen wake lock while `enabled` is true so the device (typically
 * a warehouse handheld like the Urovo CT-58S) doesn't dim / sleep during a
 * scan session. The browser silently releases the lock when the tab goes
 * to the background — we re-acquire it on `visibilitychange`.
 *
 * Uses the standard Screen Wake Lock API. On browsers that don't ship it
 * (older WebViews, Firefox without the flag) the hook simply no-ops.
 */
export function useScreenWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    const api = (navigator as Navigator & { wakeLock: { request(type: 'screen'): Promise<WakeLockSentinel> } }).wakeLock;

    let cancelled = false;
    let lock: WakeLockSentinel | null = null;

    async function acquire() {
      if (cancelled) return;
      try {
        const next = await api.request('screen');
        if (cancelled) {
          next.release().catch(() => {});
          return;
        }
        lock = next;
      } catch {
        // Browser refused (tab hidden, low-power mode, etc.) — silent
      }
    }

    function handleVisibility() {
      if (document.visibilityState === 'visible' && !lock) acquire();
    }

    acquire();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (lock) {
        lock.release().catch(() => {});
        lock = null;
      }
    };
  }, [enabled]);
}
